import React, { useCallback, useMemo, useState } from 'react'
import axios from 'axios'
import TicketDetailsListItem from './TicketDetailsListItem'
import TicketBotNote from './TicketBotNote'
import { isBotDetail } from '../../constants/ticket'
import { ConfirmDelete, MwSpinnerButton, MwToast } from '../ui'
import { useFetch } from '../../hooks'

const TicketDetailsList = ({ data, loading, ticket, supportSettings, onRefresh }) => {
    const { data: adminsResult } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins`);
    const [detailDeleteTarget, setDetailDeleteTarget] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [toastMsg, setToastMsg] = useState('');
    const [showToast, setShowToast] = useState(false);

    const adminById = useMemo(() => {
        const admins = adminsResult?.data || [];
        const map = {};
        admins.forEach((admin) => {
            const id = admin?._id ? String(admin._id).trim() : '';
            if (!id) return;
            map[id] = {
                name: admin?.name || admin?.username || admin?.email || id,
                jobTitle: admin?.jobTitle || '',
                avatar: admin?.userPic || ''
            };
        });
        return map;
    }, [adminsResult]);

    const requestDeleteDetail = useCallback((detail) => {
        const detailId = detail?._id ? String(detail._id).trim() : '';
        const ticketId = ticket?._id ? String(ticket._id).trim() : '';
        if (!detailId || !ticketId) return;
        setDetailDeleteTarget({ ticketId, detailId });
        setShowDeleteConfirm(true);
    }, [ticket?._id]);

    const deleteDetailHandler = useCallback(async () => {
        const ticketId = detailDeleteTarget?.ticketId ? String(detailDeleteTarget.ticketId).trim() : '';
        const detailId = detailDeleteTarget?.detailId ? String(detailDeleteTarget.detailId).trim() : '';
        if (!ticketId || !detailId) return;
        setDeleteLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
            const response = await axios.delete(
                `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/${ticketId}/details/${detailId}`,
                { headers, validateStatus: () => true }
            );
            if (response?.status >= 200 && response?.status < 300 && response?.data?.status === 1) {
                setToastMsg('تم حذف الرد بنجاح');
                setShowToast(true);
                setShowDeleteConfirm(false);
                setDetailDeleteTarget(null);
                if (typeof onRefresh === 'function') {
                    onRefresh();
                }
                return;
            }
            setToastMsg(response?.data?.message || 'فشل حذف الرد');
            setShowToast(true);
        } catch {
            setToastMsg('فشل حذف الرد');
            setShowToast(true);
        } finally {
            setDeleteLoading(false);
        }
    }, [detailDeleteTarget?.detailId, detailDeleteTarget?.ticketId, onRefresh]);

    return (
        <>
            {
                loading ? <MwSpinnerButton isFullCenter={true} withLabel={false} /> :
                    <div className='bg-white p-6 rounded-2xl shadow-sm border border-slate-100'>
                        <MwToast msg={toastMsg} isShow={showToast} setIsShow={setShowToast} />
                        <div className='pb-4 text-sm font-bold text-slate-700'>
                            الردود والمحادثة
                        </div>
                        {
                            data?.length > 0 ? data.map((item) => isBotDetail(item) ? (
                                <TicketBotNote key={item._id} item={item} supportSettings={supportSettings} />
                            ) : (
                                <TicketDetailsListItem
                                    key={item._id}
                                    item={item}
                                    ticket={ticket}
                                    supportSettings={supportSettings}
                                    onRefresh={onRefresh}
                                    adminById={adminById}
                                    onRequestDelete={requestDeleteDetail}
                                />
                            )) : (
                                <div className='text-sm text-slate-400 py-4'>
                                    لا توجد ردود حتى الآن
                                </div>
                            )
                        }
                        <ConfirmDelete
                            title='حذف الرد'
                            msg='سيتم حذف هذا الرد من المحادثة ولا يمكن التراجع عن هذا الإجراء.'
                            onShow={showDeleteConfirm}
                            setOnShow={setShowDeleteConfirm}
                            loading={deleteLoading}
                            onDeleteHandler={deleteDetailHandler}
                            confirmLabel='حذف الرد'
                        />
                    </div>
            }
        </>
    )
}

export default TicketDetailsList
